import { action, computed, makeObservable, observable } from 'mobx';

/**
 * @description Load state data.
 */
enum LoadState {
    /**
     * Data is not loaded.
     */
    NOT_LOADED,

    /**
     * Data is loading.
     */
    LOADING,

    /**
     * Data is loaded.
     */
    LOADED,

    /**
     * Error.
     */
    HAS_ERROR,
}

/**
 * @description Callback for loading data .
 */
type LoadDataCallback<T> = () => Promise<T>;

/**
 * @description Data loading status store.
 */
export class LoadStateStore {
    @observable
    private state: LoadState = LoadState.NOT_LOADED;

    private readonly includeLoadingState: boolean;

    /**
     * @param {boolean} includeLoadingState - Is need loading state.
     */
    constructor(includeLoadingState = true) {
        this.state = LoadState.NOT_LOADED;
        this.includeLoadingState = includeLoadingState;
        makeObservable(this);
    }

    /**
     * @description Has error.
     * @returns {boolean} Value.
     */
    @computed
    get hasError() {
        return this.state === LoadState.HAS_ERROR;
    }

    /**
     * @description Is loading.
     * @returns {boolean} Value.
     */
    @computed
    get isLoading() {
        return this.state === LoadState.LOADING;
    }

    /**
     * @description Is not loaded.
     * @returns {boolean} Value.
     */
    @computed
    get isNotLoaded() {
        return this.state === LoadState.NOT_LOADED;
    }

    /**
     * @description Is loaded.
     * @returns {boolean} Value.
     */
    @computed
    get isLoaded() {
        return this.state === LoadState.LOADED;
    }

    /**
     * @description Set current load state.
     * @param {LoadState} state - Current load state.
     */
    @action
    private setState = (state: LoadState) => {
        this.state = state;
    };

    loadData = <T>(callback: LoadDataCallback<T>) => {
        if (this.includeLoadingState) {
            this.setState(LoadState.LOADING);
        }

        return callback()
            .then((data) => {
                this.setState(LoadState.LOADED);
                return data;
            })
            .catch((error) => {
                this.setState(LoadState.HAS_ERROR);
                throw error;
            });
    };
}
